/**
 * Variation Order Register — portfolio VOR with approval workflow + Cognara scope-creep + CBS re-baseline.
 */
import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { GitBranch, Sparkles, Loader2, ArrowLeft, RefreshCw, CheckCircle2, Search } from 'lucide-react';
import api from '@/lib/api';

const fmtINR = (v) => v == null ? '—' : (Math.abs(v) >= 1e7 ? `₹${(v / 1e7).toFixed(2)} Cr` : Math.abs(v) >= 1e5 ? `₹${(v / 1e5).toFixed(2)} L` : `₹${Number(v).toLocaleString('en-IN')}`);
const verdictColor = (v) => ({ GENUINE: '#22C55E', MIXED: '#F59E0B', SCOPE_CREEP: '#EF4444' })[v] || '#94A3B8';
const statusColor = (s) => ({ PENDING: '#F59E0B', UNDER_REVIEW: '#3B82F6', APPROVED: '#22C55E', REJECTED: '#EF4444', REBASELINED: '#8B5CF6' })[s] || '#94A3B8';

export default function ContractsVariations() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected, setSelected] = useState(null);
  const [showScope, setShowScope] = useState(false);
  const [scoping, setScoping] = useState(false);
  const [scope, setScope] = useState(null);
  const [rebaselining, setRebaselining] = useState(null);

  const load = async () => {
    try { const r = await api.get('/contracts/portfolio/variations'); setData(r.data); }
    catch { toast.error('Failed to load variations'); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const rows = data?.variations || [];
  const filtered = useMemo(() => rows.filter(r => {
    const t = q.toLowerCase();
    const okQ = !t || (r.title || '').toLowerCase().includes(t) || (r.variation_number || '').toLowerCase().includes(t) || (r.project || '').toLowerCase().includes(t);
    const okS = statusFilter === 'all' || r.status === statusFilter;
    return okQ && okS;
  }), [rows, q, statusFilter]);

  const checkScope = async (v) => {
    setSelected(v); setShowScope(true); setScope(null); setScoping(true);
    try { const r = await api.post(`/contracts/variations/${v.id}/scope-creep-check`); setScope(r.data); toast.success('Analysis ready'); }
    catch { toast.error('Analysis failed'); } finally { setScoping(false); }
  };

  const rebaseline = async (v) => {
    if (v.status !== 'APPROVED') return toast.error('Variation must be APPROVED first');
    if (!confirm(`Push ₹${fmtINR(v.approved_amount || v.requested_amount)} to CBS as baseline delta?`)) return;
    setRebaselining(v.id);
    try { await api.post(`/contracts/variations/${v.id}/rebaseline-cbs`); toast.success('CBS re-baselined'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Re-baseline failed'); }
    finally { setRebaselining(null); }
  };

  const c = data?.counts || {}; const totals = data?.totals || {};

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-96"><Loader2 className="w-8 h-8 animate-spin text-blue-500" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="contracts-variations">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate('/contracts')} data-testid="var-back-btn"><ArrowLeft className="w-4 h-4" /></Button>
          <div>
            <h1 className="text-2xl font-bold text-foreground">Variation Order Register</h1>
            <p className="text-sm text-muted-foreground mt-1">Every variation · approval workflow · Cognara scope-creep detection · CBS re-baseline</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="var-kpis">
          {[
            { label: 'Total', value: c.total || 0, color: '#94A3B8' },
            { label: 'Pending', value: c.pending || 0, color: '#F59E0B' },
            { label: 'Approved', value: c.approved || 0, color: '#22C55E' },
            { label: 'Total Value', value: fmtINR(totals.total), color: '#8B5CF6' },
            { label: 'Approved Value', value: fmtINR(totals.approved), color: '#22C55E' },
          ].map(kpi => (
            <Card key={kpi.label} className="bg-card border-border"><CardContent className="p-3">
              <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{kpi.label}</p>
              <p className="text-xl font-bold" style={{ color: kpi.color }}>{kpi.value}</p>
            </CardContent></Card>
          ))}
        </div>

        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3 flex-wrap">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Search title / number / project..." className="pl-10 bg-muted border-border text-foreground" data-testid="var-search" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-48 bg-muted border-border text-foreground" data-testid="var-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  {['all', 'PENDING', 'UNDER_REVIEW', 'APPROVED', 'REJECTED'].map(s => <SelectItem key={s} value={s}>{s === 'all' ? 'All Status' : s.replace(/_/g, ' ')}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs" data-testid="var-table">
                <thead className="text-muted-foreground uppercase text-[10px]">
                  <tr>{['Variation', 'Type', 'Project', 'Requested (₹/days)', 'Approved', 'Scope-Creep', 'Status', 'CBS Re-baseline', 'Action'].map(h => <th key={h} className="text-left px-2 py-2">{h}</th>)}</tr>
                </thead>
                <tbody>
                  {filtered.map(r => (
                    <tr key={r.id} className="border-t border-border hover:bg-white/[0.02]" data-testid={`var-row-${r.id}`}>
                      <td className="px-2 py-2 text-foreground">{r.title}<div className="text-[10px] text-muted-foreground">{r.variation_number}</div></td>
                      <td className="px-2 py-2"><Badge variant="outline" className="text-[10px]">{r.type}</Badge></td>
                      <td className="px-2 py-2 text-muted-foreground">{r.project}</td>
                      <td className="px-2 py-2 text-foreground">{fmtINR(r.requested_amount)} / {r.requested_days}d</td>
                      <td className="px-2 py-2 text-emerald-400">{fmtINR(r.approved_amount)}</td>
                      <td className="px-2 py-2">{r.scope_creep ? <Badge style={{ background: `${verdictColor(r.scope_creep.verdict)}22`, color: verdictColor(r.scope_creep.verdict) }} className="text-[9px]">{r.scope_creep.verdict}</Badge> : <span className="text-muted-foreground">—</span>}</td>
                      <td className="px-2 py-2"><Badge style={{ background: `${statusColor(r.status)}22`, color: statusColor(r.status) }} className="text-[9px]">{r.status}</Badge></td>
                      <td className="px-2 py-2">{r.cbs_rebaselined ? <span className="flex items-center gap-1 text-emerald-400 text-[10px]"><CheckCircle2 className="w-3 h-3" />Applied</span> : <span className="text-muted-foreground text-[10px]">Pending</span>}</td>
                      <td className="px-2 py-2 flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => checkScope(r)} className="h-6 px-2 text-purple-400 hover:text-purple-300" data-testid={`var-scope-${r.id}`}><Sparkles className="w-3 h-3" /></Button>
                        {r.status === 'APPROVED' && !r.cbs_rebaselined && (
                          <Button size="sm" variant="ghost" onClick={() => rebaseline(r)} disabled={rebaselining === r.id} className="h-6 px-2 text-blue-400 hover:text-blue-300" data-testid={`var-rebaseline-${r.id}`}>
                            {rebaselining === r.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && <tr><td colSpan={9} className="text-center py-6 text-muted-foreground">No variations match filters</td></tr>}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Dialog open={showScope} onOpenChange={setShowScope}>
          <DialogContent className="max-w-xl bg-card border-border text-foreground" data-testid="var-scope-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-purple-400" />Cognara Scope-Creep Analysis — {selected?.variation_number}</DialogTitle>
              <DialogDescription>Is this a genuine variation or scope creep?</DialogDescription>
            </DialogHeader>
            {scoping ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-purple-500 mr-2" /><span className="text-sm text-muted-foreground">Analyzing...</span></div>
            ) : scope ? (
              <div className="space-y-3 text-sm" data-testid="var-scope-content">
                <div className="rounded p-3 text-center" style={{ background: 'var(--muted)', border: `1px solid ${verdictColor(scope.verdict)}44` }}>
                  <p className="text-3xl font-bold" style={{ color: verdictColor(scope.verdict) }}>{scope.verdict}</p>
                  <p className="text-xs text-muted-foreground mt-1">{scope.confidence_pct}% confidence</p>
                </div>
                <div className="rounded p-3" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>{scope.analysis}</div>
                {scope.red_flags?.length > 0 && <div>
                  <p className="text-xs uppercase tracking-widest text-red-400 mb-1">Red Flags</p>
                  <ul className="list-disc list-inside text-xs text-foreground/80 space-y-0.5">{scope.red_flags.map((f, i) => <li key={i}>{f}</li>)}</ul>
                </div>}
                <div className="rounded p-3" style={{ background: 'var(--muted)', border: '1px solid var(--border)' }}>
                  <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1">Recommended</p>
                  <p className="text-foreground font-semibold">{scope.recommended_action?.replace(/_/g, ' ')}</p>
                  {scope.conditions?.length > 0 && <ul className="list-disc list-inside text-xs text-foreground/80 space-y-0.5 mt-2">{scope.conditions.map((c, i) => <li key={i}>{c}</li>)}</ul>}
                </div>
              </div>
            ) : null}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
